import React, { useState, useRef, useEffect, useCallback } from 'react';
import { chatAPI } from '../api';
import imageCompression from 'browser-image-compression';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  image?: string; // base64 preview for user-sent images
}

interface ChatProps {
  onMealItemsUpdated?: () => void; // Notify parent to refresh meal items
  onMealItemAdded?: (item: any) => void; // Immediate callback when item is added
  selectedDate?: string; // Date for which to add items (YYYY-MM-DD format)
}

const Chat: React.FC<ChatProps> = ({ onMealItemsUpdated, onMealItemAdded, selectedDate }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentResponse, setCurrentResponse] = useState('');
  const [conversationId, setConversationId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      // Scroll the chat container to bottom, not the whole page
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentResponse]);

  const generateConversationId = () => {
    return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  useEffect(() => {
    // Generate conversation ID when component mounts
    if (!conversationId) {
      setConversationId(generateConversationId());
    }
  }, [conversationId]);

  const sendMessage = async (message: string, isAutomatic = false) => {
    setIsLoading(true);

    if (!isAutomatic) {
      const newUserMessage: Message = {
        id: Date.now().toString(),
        content: message || (imagePreview ? '[Image]' : ''),
        role: 'user',
        timestamp: new Date(),
        image: imagePreview || undefined,
      };
      setMessages(prev => [...prev, newUserMessage]);
    }
    
    setCurrentResponse('');

    try {
      console.log('[DEBUG] Sending message with conversation ID:', conversationId);

      // 1. If an image is selected, upload it first to obtain the public URL
      let imageUrl: string | undefined;
      if (selectedImage) {
        try {
          imageUrl = await chatAPI.uploadImage(selectedImage);
          console.log('[DEBUG] Image uploaded, public URL:', imageUrl);
        } catch (uploadErr) {
          console.error('Failed to upload image:', uploadErr);
          // Fallback: proceed without image
          imageUrl = undefined;
        }
      }

      // 2. Prepare message history (last 12 messages, formatted as role + content)
      const formattedHistory = messages.slice(-12).map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      // 3. Open the SSE stream (unified endpoint for both food logging and coaching)
      const stream = await chatAPI.streamMessage(message, conversationId, selectedDate, imageUrl, formattedHistory);
      const reader = stream.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.content) {
                fullResponse += data.content;
                setCurrentResponse(fullResponse);
              }
              // Handle tool results for immediate UI updates
              if (data.toolResult && data.toolResult.type === 'add_item' && onMealItemAdded) {
                const toolResultData = data.toolResult.data;
                if (toolResultData.success && toolResultData.items) {
                  // Immediately add each new item to the parent's list
                  toolResultData.items.forEach((item: any) => {
                    onMealItemAdded(item);
                  });
                }
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: fullResponse,
        role: 'assistant',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);
      setCurrentResponse('');

      // Notify parent that meal items may have been updated
      if (onMealItemsUpdated) {
        onMealItemsUpdated();
      }

      // Keep conversationId persistent across messages for conversation continuity
      // (it's only generated once on mount)

      // Reset image after sending
      setSelectedImage(null);
      setImagePreview(null);
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: 'Sorry, I encountered an error. Please try again.',
        role: 'assistant',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!input.trim() && !selectedImage) || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    await sendMessage(userMessage);
  };

  const formatMessage = (content: string) => {
    // Simple markdown-like formatting
    return content
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('**') && line.endsWith('**')) {
          return <div key={index} className="font-bold text-gray-900 mt-2 mb-1 text-sm sm:text-base">{line.slice(2, -2)}</div>;
        }
        if (line.startsWith('- ')) {
          return <div key={index} className="ml-3 sm:ml-4 text-sm sm:text-base leading-relaxed">{line}</div>;
        }
        return <div key={index} className="text-sm sm:text-base leading-relaxed">{line || <br />}</div>;
      });
  };

  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleImageSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Compress image so that its longest side is at most 500px
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 500,
        useWebWorker: true,
      });

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64data = reader.result as string;
        setSelectedImage(compressed);
        setImagePreview(base64data);
      };
      reader.readAsDataURL(compressed);
    } catch (err) {
      console.error('Image compression failed', err);
    }
  }, []);

  const removeSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  // Auto-resize the textarea as the user types (up to 4 lines ≈ 160px)
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const maxHeight = 160; // px (≈4 lines with current text sizing)
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    }
  }, [input]);

  return (
    <div className="flex flex-col h-80 sm:h-96 bg-gradient-to-b from-gray-50 to-white border border-gray-200/50 rounded-2xl overflow-hidden">
      <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 py-6 sm:py-8">
            <div className="text-3xl sm:text-4xl mb-3">🍽️💬</div>
            <p className="text-sm sm:text-base font-medium">Log food or get nutrition advice!</p>
            <p className="text-xs sm:text-sm mt-2 text-gray-400">Try: "I had 2 eggs" or "What should I eat for dinner?"</p>
          </div>
        )}
        
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] sm:max-w-xs lg:max-w-md px-3 sm:px-4 py-2 sm:py-3 rounded-2xl shadow-sm ${
                message.role === 'user'
                  ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
                  : 'bg-white border border-gray-200 text-gray-900'
              }`}
            >
              <div className="text-sm sm:text-base">
                {message.role === 'assistant' ? formatMessage(message.content) : message.content}
                {message.image && (
                  <img src={message.image} alt="sent" className="mt-2 rounded-lg border max-w-[200px]" />
                )}
              </div>
              {message.role === 'assistant' && (
                <div className="text-xs text-gray-400 mt-2 opacity-70">
                  {formatTimestamp(message.timestamp)}
                </div>
              )}
            </div>
          </div>
        ))}
        
        {currentResponse && (
          <div className="flex justify-start">
            <div className="max-w-[85%] sm:max-w-xs lg:max-w-md px-3 sm:px-4 py-2 sm:py-3 rounded-2xl bg-white border border-gray-200 text-gray-900 shadow-sm">
              <div className="text-sm sm:text-base">
                {formatMessage(currentResponse)}
                <span className="inline-block w-1.5 h-4 bg-blue-500 ml-1 animate-pulse rounded-full" />
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      
      <form onSubmit={handleSendMessage} className="border-t border-gray-200/50 p-3 sm:p-4 bg-white">
        <div className="flex items-end space-x-2 sm:space-x-3">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!isLoading && (input.trim() || imagePreview)) {
                  handleSendMessage(e as any);
                }
              }
            }}
            rows={1}
            placeholder="Log food or ask for nutrition advice..."
            disabled={isLoading}
            className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 text-sm sm:text-base placeholder-gray-400 resize-none overflow-y-auto max-h-40"
          />
          <div className="relative">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              id="chat-image-input"
              onChange={handleImageSelect}
              disabled={isLoading || !!imagePreview}
            />
            <label htmlFor="chat-image-input" className={`cursor-pointer px-2 py-2 rounded-full hover:bg-gray-100 ${imagePreview ? 'opacity-50 cursor-not-allowed' : ''}`}>
              📷
            </label>
          </div>
          {imagePreview && (
            <button type="button" onClick={removeSelectedImage} className="text-red-500 text-lg">✕</button>
          )}
          <button
            type="submit"
            disabled={isLoading || (!input.trim() && !imagePreview)}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 font-medium text-sm sm:text-base min-w-[60px] sm:min-w-[80px]"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
            ) : (
              'Send'
            )}
          </button>
        </div>

        {imagePreview && (
          <div className="flex items-center mt-3 space-x-2">
            <img src={imagePreview} alt="preview" className="w-16 h-16 object-cover rounded-lg border" />
            <span className="text-xs text-gray-500">Image attached</span>
          </div>
        )}
      </form>
    </div>
  );
};

export default Chat;